import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../utils/supabase';
import { useToast } from '../context/ToastContext';
import { createLogger } from '../utils/logger';
import { Note, Review } from '../types/items';
import { CareerCard } from '../types/careerCard';
import { EditState } from '../types/editState';
import { useAuthState } from '../hooks/useAuthState';

const logger = createLogger({ module: 'useAutoSave' });

const AUTO_SAVE_INTERVAL = 30000; // 30 seconds
const DRAFT_RETENTION_DAYS = 14; // Keep drafts for 14 days

type DraftableItem = Note | Review | CareerCard;

interface AutoSaveState {
  lastSaved: Date | null;
  isSaving: boolean;
  error: string | null;
}

export function useAutoSave<T extends DraftableItem>(
  editState: EditState<T> | null,
  onSave: (item: T) => Promise<T>,
  options: {
    enabled?: boolean;
    interval?: number;
  } = {}
) {
  const { showToast } = useToast();
  const { user, isAuthenticated } = useAuthState();
  const [autoSaveState, setAutoSaveState] = useState<AutoSaveState>({
    lastSaved: null,
    isSaving: false,
    error: null,
  });
  const timeoutRef = useRef<NodeJS.Timeout>(undefined);
  const isEnabled = options.enabled ?? true;
  const interval = options.interval ?? AUTO_SAVE_INTERVAL;

  // Keep track of the last successful save to avoid unnecessary saves
  const lastSuccessfulSaveRef = useRef<string>('');

  const saveDraft = useCallback(async () => {
    if (!editState?.currentContent || !editState.hasUnsavedChanges || !isAuthenticated || !user) {
      return;
    }

    const currentContent = editState.currentContent;
    const contentString = JSON.stringify(currentContent);

    // Skip if content hasn't changed since last save
    if (contentString === lastSuccessfulSaveRef.current) {
      return;
    }

    setAutoSaveState(prev => ({ ...prev, isSaving: true, error: null }));

    try {
      // Determine item type
      let itemType: 'note' | 'review' | 'career_card';
      if ('card_data' in currentContent) {
        itemType = 'career_card';
      } else if ('review_text' in currentContent) {
        itemType = 'review';
      } else {
        itemType = 'note';
      }

      // Get existing draft
      const { data: existingDrafts } = await supabase
        .from('drafts')
        .select('id')
        .eq('user_id', user.id)
        .eq('item_id', currentContent.id || null)
        .eq('item_type', itemType)
        .in('status', ['draft', 'auto_saved'])
        .limit(1);

      if (existingDrafts && existingDrafts.length > 0) {
        // Update existing draft
        const { error } = await supabase
          .from('drafts')
          .update({
            content: currentContent,
            last_saved: new Date().toISOString(),
            original_version: editState.originalContent,
          })
          .eq('id', existingDrafts[0].id);

        if (error) throw error;
      } else {
        // Create new draft
        const { error } = await supabase.from('drafts').insert({
          user_id: user.id,
          item_id: currentContent.id || null,
          item_type: itemType,
          content: currentContent,
          original_version: editState.originalContent,
          status: 'auto_saved',
        });

        if (error) throw error;
      }

      lastSuccessfulSaveRef.current = contentString;
      setAutoSaveState(prev => ({
        ...prev,
        lastSaved: new Date(),
        isSaving: false,
        error: null,
      }));

      showToast('Draft saved', 'success');
    } catch (error) {
      logger.error('Error auto-saving draft:', error);
      setAutoSaveState(prev => ({
        ...prev,
        isSaving: false,
        error: error instanceof Error ? error.message : 'Failed to save draft',
      }));

      if (navigator.onLine) {
        showToast('Failed to save draft', 'error');
      }
    }
  }, [editState, showToast, user, isAuthenticated]);

  // Set up auto-save interval
  useEffect(() => {
    if (!isEnabled || !editState || !isAuthenticated || !user) {
      return;
    }

    const setupAutoSave = () => {
      timeoutRef.current = setTimeout(async () => {
        if (editState.hasUnsavedChanges && !autoSaveState.isSaving) {
          await saveDraft();
        }
        setupAutoSave(); // Set up next interval
      }, interval);
    };

    setupAutoSave();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isEnabled, interval, editState, autoSaveState.isSaving, saveDraft, isAuthenticated, user]);

  // Save before window/tab close
  useEffect(() => {
    if (!isEnabled || !isAuthenticated || !user) return;

    const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
      if (editState?.hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
        
        try {
          await saveDraft();
        } catch (error) {
          logger.error('Error saving draft before unload:', error);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isEnabled, editState, saveDraft, isAuthenticated, user]);

  // Clean up old drafts
  useEffect(() => {
    const cleanupOldDrafts = async () => {
      if (!isAuthenticated || !user) return;
      
      try {
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - DRAFT_RETENTION_DAYS);

        await supabase
          .from('drafts')
          .delete()
          .lt('last_saved', cutoffDate.toISOString())
          .in('status', ['draft', 'auto_saved']);
      } catch (error) {
        logger.error('Error cleaning up old drafts:', error);
      }
    };

    // Run cleanup when hook is first mounted
    cleanupOldDrafts();
  }, [isAuthenticated, user]);

  return {
    lastSaved: autoSaveState.lastSaved,
    isSaving: autoSaveState.isSaving,
    error: autoSaveState.error,
    saveDraft,
  };
} 